import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Button, 
  Grid,
  List,
  ListItem,
  ListItemContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Modal,
  ModalDialog,
  IconButton,
  Tooltip,
  useTheme,
  Accordion,
  AccordionGroup,
  AccordionSummary,
  AccordionDetails
} from '@mui/joy';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaTrash, 
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBuilding,
  FaCalendar,
  FaEye,
  FaPlus
} from 'react-icons/fa';
import { useCustomerStore } from '../store/customerStore';
import { useProjects } from '../hooks/useProjects';
import { IconWrapper } from '../components/IconWrapper';
import { CustomerEditForm } from '../components/CustomerEditForm';
import StreetViewEmbed from '../components/StreetViewEmbed';
import type { Customer } from '../services/customerService';
import { customerExtraDetails } from '../services/customerMapping';
import type { Project } from '../types/project';
import { loadWorkspaceSnapshots } from './project-workspace/workspaceDashboard.service';
import type { WorkspaceSnapshot } from './project-workspace/workspaceDashboard.model';
import { stateShortLabel, workspaceStageLabel, type WorkspaceStage } from './project-workspace/workspaceStages';

import { documentsService } from '../modules/docs/services/documents.service';
import { listDocumentShareLinks, type DocumentShareLinkRecord } from '../modules/docs/services/documentShareLinks.service';
import CustomerCallRecordingsCard from './CustomerCallRecordingsCard';

const STAGE_COLOR: Record<WorkspaceStage, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'> = {
  presale: 'neutral',
  deposit: 'warning',
  ordering: 'primary',
  installation: 'primary',
  aftercare: 'success',
  completed: 'success',
  cancelled: 'neutral',
};

const CustomerDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { customerName } = useParams<{ customerName: string }>();
  const { customers, loading: customersLoading, error: customersError, deleteLocalCustomer, loadCustomers } = useCustomerStore();
  const { projects, loading: projectsLoading } = useProjects();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerProjects, setCustomerProjects] = useState<Project[]>([]);
  // Where each of the customer's workspaces is: the Workspaces Board's answer
  const [projectSnapshots, setProjectSnapshots] = useState<Record<string, WorkspaceSnapshot>>({});
  useEffect(() => {
    if (!customerProjects.length) {
      setProjectSnapshots({});
      return;
    }
    let cancelled = false;
    loadWorkspaceSnapshots(customerProjects as any)
      .then((snapshots) => { if (!cancelled) setProjectSnapshots(snapshots); })
      .catch((err) => console.warn('Failed to load workspace stages', err));
    return () => { cancelled = true; };
  }, [customerProjects]);
  const [jarvisDocuments, setJarvisDocuments] = useState<any[]>([]);
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const [transmissions, setTransmissions] = useState<Array<DocumentShareLinkRecord & {
    documentId: string;
    documentKind: string;
    lifecycleRef: string;
    documentTitle: string;
  }>>([]);
  const [transmissionsLoading, setTransmissionsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load customers if not already loaded
  useEffect(() => {
    if (customers.length === 0 && !customersLoading) {
      loadCustomers();
    }
  }, [customers.length, customersLoading, loadCustomers]);

  // Find customer by name (URL parameter)
  useEffect(() => {
    if (customerName && customers.length > 0) {
      const decodedName = decodeURIComponent(customerName);
      console.log('Looking for customer:', decodedName);
      console.log('Available customers:', customers.map(c => c.name));
      
      const foundCustomer = customers.find(c => 
        c.name.toLowerCase() === decodedName.toLowerCase()
      );
      
      console.log('Found customer:', foundCustomer);
      setCustomer(foundCustomer || null);
    }
  }, [customerName, customers]);

  // Find projects for this customer
  useEffect(() => {
    if (customer && projects.length > 0) {
      console.log('CustomerDetailPage: Finding projects for customer:', {
        customerId: customer.id,
        customerName: customer.name,
        customerEmail: customer.email,
        customerSource: customer.source,
        projectsCount: projects.length,
        projects: projects.slice(0, 3) // First 3 for debugging
      });
      
      let customerProjects: Project[] = [];
      if (customer.source === 'local') {
        // For local customers, match by customer_id
        customerProjects = projects.filter(project => {
          const match = project.customer_id?.toString() === customer.id;
          console.log('Local customer matching:', {
            projectId: project.id,
            projectCustomerId: project.customer_id,
            customerId: customer.id,
            match
          });
          return match;
        });
      } else if (customer.source === 'api' || customer.source === 'external') {
        // For API/external customers, match by email if available, otherwise by name
        if (customer.email) {
          customerProjects = projects.filter(project => {
            const match = project.customer_email && project.customer_email.toLowerCase() === customer.email!.toLowerCase();
            console.log('External customer email matching:', {
              projectId: project.id,
              projectCustomerEmail: project.customer_email,
              customerEmail: customer.email,
              match
            });
            return match;
          });
        } else {
          customerProjects = projects.filter(project => {
            const match = project.customer_name && project.customer_name.toLowerCase() === customer.name.toLowerCase();
            console.log('External customer name matching:', {
              projectId: project.id,
              projectCustomerName: project.customer_name,
              customerName: customer.name,
              match
            });
            return match;
          });
        }
      }
      
      console.log('CustomerDetailPage: Found projects:', customerProjects.length);
      setCustomerProjects(customerProjects);
    }
  }, [customer, projects]);

  // Fetch JARVIS documents for this customer (project agnostic)
  useEffect(() => {
    const fetchJarvisDocs = async () => {
      if (!customer?.id) return;
      setJarvisLoading(true);
      try {
        const docs = await documentsService.getDocumentsForCustomer(customer.id);
        setJarvisDocuments(docs);
      } catch (e) {
        console.error('Error fetching JARVIS documents for customer:', e);
      } finally {
        setJarvisLoading(false);
      }
    };
    fetchJarvisDocs();
  }, [customer?.id]);

  // Fetch public-doc transmissions for this customer from all their documents
  useEffect(() => {
    const fetchTransmissions = async () => {
      if (!customer?.id || jarvisDocuments.length === 0) {
        setTransmissions([]);
        return;
      }
      setTransmissionsLoading(true);
      try {
        const perDocument = await Promise.all(
          jarvisDocuments.map(async (doc: any) => {
            const links = await listDocumentShareLinks(String(doc.id));
            return links.map((link) => ({
              ...link,
              documentId: String(doc.id),
              documentKind: String(doc.kind || 'Document'),
              lifecycleRef: String(doc.kind === 'Invoice' && doc.invoiceRef ? doc.invoiceRef : doc.lifecycleRef || ''),
              documentTitle: String(doc.title || 'Untitled'),
            }));
          })
        );
        const flattened = perDocument
          .flat()
          .sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt));
        setTransmissions(flattened);
      } catch (error) {
        console.error('Error fetching customer transmissions:', error);
        setTransmissions([]);
      } finally {
        setTransmissionsLoading(false);
      }
    };
    fetchTransmissions();
  }, [customer?.id, jarvisDocuments]);

  const handleDeleteCustomer = async () => {
    if (!customer || customer.source !== 'local') {
      return;
    }

    setDeleting(true);
    try {
      await deleteLocalCustomer(customer.id);
      navigate('/directory');
    } catch (error) {
      console.error('Error deleting customer:', error);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (customersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (customersError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="danger">{customersError}</Alert>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="warning">Customer not found</Alert>
        <Button 
          component={Link} 
          to="/directory" 
          startDecorator={<IconWrapper icon={FaArrowLeft} />}
          sx={{ mt: 2 }}
        >
          Back to Directory
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ mb: 3, pt: { xs: 0, md: 2 } }}>
        <Button 
          component={Link} 
          to="/directory" 
          startDecorator={<IconWrapper icon={FaArrowLeft} />}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Back to Directory
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography level="h2" sx={{ fontSize: { xs: 'xl', md: 'h2' }, color: '#111', mb: 1 }}>
              {customer.name}
            </Typography>
            <Chip 
              color={customer.source === 'api' ? 'primary' : 'success'} 
              size="sm" 
              variant="soft"
            >
              {customer.source === 'api' ? 'API Customer' : 'Local Customer'}
            </Chip>
          </Box>
          
          <Stack direction="row" spacing={1}>
            {customer.source === 'local' && (
              <>
                <Button
                  startDecorator={<IconWrapper icon={FaEdit} />}
                  onClick={() => setShowEditModal(true)}
                  size="sm"
                >
                  Edit
                </Button>
                <Button
                  startDecorator={<IconWrapper icon={FaTrash} />}
                  color="danger"
                  variant="outlined"
                  onClick={() => setShowDeleteConfirm(true)}
                  size="sm"
                >
                  Delete
                </Button>
              </>
            )}
            <Button
              startDecorator={<IconWrapper icon={FaPlus} />}
              onClick={() => navigate('/projects/new', { state: { customerId: customer.id, customerName: customer.name } })}
              size="sm"
            >
              New Project
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Customer Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2, color: '#111' }}>
            Customer Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                    <IconWrapper icon={FaPhone} /> Phone
                  </Typography>
                  <Typography 
                    level="body-md"
                    component="a"
                    href={`tel:${customer.phone}`}
                    sx={{
                      color: 'primary.600',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        color: 'primary.700',
                      }
                    }}
                  >
                    {customer.phone}
                  </Typography>
                </Box>
                
                {customer.email && (
                  <Box>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                      <IconWrapper icon={FaEnvelope} /> Email
                    </Typography>
                    <Typography level="body-md">
                      {customer.email}
                    </Typography>
                  </Box>
                )}

                {customerExtraDetails(customer).map((item) => (
                  <Box key={item.key}>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    {item.href ? (
                      <Typography
                        level="body-md"
                        component="a"
                        href={item.href}
                        sx={{
                          color: 'primary.600',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': { color: 'primary.700' },
                        }}
                      >
                        {item.value}
                      </Typography>
                    ) : (
                      <Typography level="body-md">{item.value}</Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Grid>
            
            <Grid xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                    <IconWrapper icon={FaMapMarkerAlt} /> Address
                  </Typography>
                  <Typography 
                    level="body-md"
                    component="a"
                    href={`https://www.google.com/maps?q=${encodeURIComponent(customer.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.600',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:hover': {
                        color: 'primary.700',
                      }
                    }}
                  >
                    {customer.address}
                  </Typography>
                </Box>
                
                {customer.company && (
                  <Box>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                      <IconWrapper icon={FaBuilding} /> Company
                    </Typography>
                    <Typography level="body-md">
                      {customer.company}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* Google Maps iframe */}
            {customer.address && (
              <Grid xs={12} md={6}>
                <Box sx={{ mt: 2 }}>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                    Google Maps
                  </Typography>
                  <Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <iframe
                      title="Google Maps"
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(customer.address)}&t=m&z=15&output=embed&iwloc=near`}
                    />
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography level="body-sm" color="neutral">
                Created: {new Date(customer.created_at).toLocaleDateString()}
              </Typography>
              <Typography level="body-sm" color="neutral">
                Last Updated: {new Date(customer.updated_at).toLocaleDateString()}
              </Typography>
            </Box>
            

          </Box>
        </CardContent>
      </Card>

      {/* Projects Section */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="h4" sx={{ color: '#111' }}>
              Projects
            </Typography>
            <Button
              startDecorator={<IconWrapper icon={FaPlus} />}
              onClick={() => navigate('/projects/new', { state: { customerId: customer.id, customerName: customer.name } })}
              size="sm"
            >
              New Project
            </Button>
          </Box>
          
          {projectsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size="lg" />
            </Box>
          ) : customerProjects.length === 0 ? (
            <Alert color="neutral">
              No projects found for this customer.
            </Alert>
          ) : (
            <List>
              {customerProjects.map((project) => {
                const snapshot = projectSnapshots[project.id] || null;
                
                return (
                  <ListItem
                    key={project.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        borderColor: 'primary.500',
                        backgroundColor: 'background.level1'
                      }
                    }}
                  >
                    <ListItemContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography level="title-md" sx={{ mb: 0.5 }}>
                            {project.project_name || 'Unnamed Project'}
                          </Typography>
                          <Typography level="body-sm" color="neutral">
                            {project.product_types?.join(', ') || 'No product types specified'}
                          </Typography>
                        </Box>
                        
                        <Stack direction="row" spacing={1} alignItems="center">
                          {snapshot && (
                            <Chip color={snapshot.exception ? 'danger' : STAGE_COLOR[snapshot.stage]} size="sm" variant="soft">
                              {[workspaceStageLabel(snapshot.stage), stateShortLabel(snapshot.state)].filter(Boolean).join(' · ')}
                            </Chip>
                          )}
                          <IconButton
                            component={Link}
                            to={`/projects/${project.id}/workspace`}
                            size="sm"
                            variant="outlined"
                          >
                            <IconWrapper icon={FaEye} />
                          </IconButton>
                        </Stack>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={2}>
                        </Stack>
                        
                        <Typography level="body-sm" color="neutral">
                          Updated: {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : 'Unknown'}
                        </Typography>
                      </Box>
                    </ListItemContent>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* JARVIS Document History */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2, color: '#111' }}>
            Document History (JARVIS)
          </Typography>
          {jarvisLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size="lg" />
            </Box>
          ) : jarvisDocuments.length === 0 ? (
            <Alert color="neutral">No JARVIS documents found for this customer.</Alert>
          ) : (
            <List>
              {jarvisDocuments.map((d: any) => (
                <ListItem key={d.id}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, '&:hover': { borderColor: 'primary.500', backgroundColor: 'background.level1' } }}
                >
                  <ListItemContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-md" sx={{ mb: 0.5 }}>
                          {d.kind} {d.lifecycleRef}
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          {d.title || 'Untitled'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="sm" variant="soft">{d.status}</Chip>
                        <Button component={Link} to={`/docs/${d.id}`} size="sm" variant="outlined">View</Button>
                      </Stack>
                    </Box>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Customer Transmissions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2, color: '#111' }}>
            Transmissions
          </Typography>
          {transmissionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size="lg" />
            </Box>
          ) : transmissions.length === 0 ? (
            <Alert color="neutral">No transmissions found for this customer yet.</Alert>
          ) : (
            <List>
              {transmissions.map((tx) => (
                <ListItem
                  key={tx.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { borderColor: 'primary.500', backgroundColor: 'background.level1' }
                  }}
                >
                  <ListItemContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-md" sx={{ mb: 0.5 }}>
                          {tx.documentKind} {tx.lifecycleRef}
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          {tx.documentTitle}
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                          Sent: {new Date(tx.sentAt).toLocaleString('en-GB')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {tx.sentVersionNumber != null && (
                          <Chip size="sm" variant="soft">v{tx.sentVersionNumber}</Chip>
                        )}
                        {tx.isOutdated && !tx.revokedAt && (
                          <Chip size="sm" color="warning" variant="soft">OUTDATED QUOTE</Chip>
                        )}
                        {tx.revokedAt && (
                          <Chip size="sm" color="danger" variant="soft">NULLIFIED</Chip>
                        )}
                        {tx.url ? (
                          <Button
                            component="a"
                            href={tx.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outlined"
                          >
                            Open Transmission
                          </Button>
                        ) : (
                          <Chip size="sm" color="neutral" variant="outlined">URL unavailable</Chip>
                        )}
                        <Button component={Link} to={`/docs/${tx.documentId}`} size="sm" variant="outlined">
                          View Doc
                        </Button>
                      </Stack>
                    </Box>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <CustomerCallRecordingsCard
        customerName={customer.name}
        phones={[customer.phone, customer.landline]}
      />

      {/* Edit Modal */}
      <CustomerEditForm
        customer={customer}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={async (id: string, updates: any) => {
          try {
            // Update the customer in the store
            await useCustomerStore.getState().updateLocalCustomer(id, updates);
            // Refresh the customer data
            await useCustomerStore.getState().loadCustomers();
            setShowEditModal(false);
          } catch (error) {
            console.error('Error updating customer:', error);
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <ModalDialog>
          <Typography level="h4" sx={{ mb: 2 }}>
            Delete Customer
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Are you sure you want to delete "{customer.name}"? This action cannot be undone.
            {customerProjects.length > 0 && (
              <Alert color="warning" sx={{ mt: 2 }}>
                This customer has {customerProjects.length} associated project(s). Deleting the customer will not delete the projects.
              </Alert>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleDeleteCustomer}
              loading={deleting}
            >
              Delete
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default CustomerDetailPage; 